import React, { useEffect, useState } from "react";
// NAVIGATION
import { useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Button, Table, Tag, Select, Tooltip, InputNumber, Input } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateBankAction,
  updateFormAction,
  updateOpenModalBankActionsHelp,
} from "../../../redux/banks/bankSlice";
import { usePutBankActionMutation } from "../../../redux/banks/bankAPI";
// COMPONENTS
import BankActionsTableActions from "./BankActionsTableActions";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableColumnBankActionType from "../../components/table/TableColumnBankActionType";
import FormInputColorPicker from "../../components/form/FormInputColorPicker";
import TableColumnLiftType from "../../components/table/TableColumnLiftType";
import SelectMessages from "../../components/select/SelectMessages";
import SelectMessagesMultiple from "../../components/select/SelectMessagesMultiple";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// CUSTOM HOOKS
import useGetOneBank from "../../../customHooks/GET_requests/useGetOneBank";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// CONSTANTS
import { BLANK_BANK_ACTION } from "../../../constants/bank";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankActionInterface } from "../../../interfaces/Bank.interface";
import {
  ACTION_TYPE_CONFIG,
  ActionButtonTypeName,
} from "../../monitoringManagement/Actions/actionTypeConfig";
import { FormAction } from "../../../types/enums";
// FLATBUFFER DISPLAY HELPERS
import { formatUuidT } from "../../../flatbuffers/display";
import { toUpdateBankActionsRequest } from "../../../redux/banks/bankActionRequest";

export default function BankActionsTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { Column, ColumnGroup } = Table;
  const { Option } = Select;
  const location = useLocation();
  const bankId = new URLSearchParams(location.search).get("idBank");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [bankActions, setBankActions] = useState<BankActionInterface[]>([]);
  const [action, setAction] = useState<BankActionInterface>({
    ...BLANK_BANK_ACTION,
  });
  const [totalRows, setTotalRows] = useState<number>(0);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPutAction,
    {
      isSuccess: isSuccessPutAction,
      isLoading: isLoadingPutAction,
      isError: isErrorPutAction,
      error: errorPutAction,
      reset: resetPutAction,
    },
  ] = usePutBankActionMutation();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { bank, isLoadingGetOneBank } = useGetOneBank({ id: bankId ?? "" });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { formAction } = useSelector((state: RootState) => state.bank);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    const copy: BankActionInterface = structuredClone(action);
    if (variableName === "timer" && typeof newValue === "number") {
      copy.timer = newValue;
    } else if (variableName === "toggleTimer" && typeof newValue === "number") {
      copy.toggleTimer = newValue;
    } else if (variableName === "color" && typeof newValue === "string") {
      copy.color = newValue;
    } else if (variableName === "toggleColor" && typeof newValue === "string") {
      copy.toggleColor = newValue;
    } else if (variableName === "label" && typeof newValue === "string") {
      copy.label = newValue;
    } else if (variableName === "toggleLabel" && typeof newValue === "string") {
      copy.toggleLabel = newValue;
    } else if (variableName === "buttonType" && typeof newValue === "string") {
      copy.buttonType = newValue as BankActionInterface["buttonType"];
    }
    setAction(copy);
  };

  const onChangeMessage = (name: string, id: string) => {
    const copy: BankActionInterface = structuredClone(action);
    copy.messageId = id;
    setAction(copy);
  };

  const onChangeToggleMessage = (name: string, id: string) => {
    const copy: BankActionInterface = structuredClone(action);
    copy.toggleMessageId = id;
    setAction(copy);
  };

  const onChangeValidStates = (ids: string[]) => {
    const copy: BankActionInterface = structuredClone(action);
    copy.validStates = ids;
    setAction(copy);
  };

  const onChangeToggleValidStates = (ids: string[]) => {
    const copy: BankActionInterface = structuredClone(action);
    copy.toggleValidStates = ids;
    setAction(copy);
  };

  const onChangeButtonType = (newValue: ActionButtonTypeName) => {
    const copy: BankActionInterface = structuredClone(action);
    copy.buttonType = newValue;
    setAction(copy);
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    // `bank.id` is a decoded FlatBuffer UuidT despite the interface declaring
    // it `string`; interpolating it raw put "[object Object]" in the URL.
    triggerPutAction({
      token,
      id: formatUuidT(bank.id),
      body: toUpdateBankActionsRequest([action], [], bank.id),
    });
  };

  const resetForm = () => {
    resetPutAction();
    setAction({ ...BLANK_BANK_ACTION });
    dispatch(updateFormAction(FormAction.Create));
    dispatch(updateBankAction({ ...BLANK_BANK_ACTION }));
  };

  const formIsValid = () => {
    if (
      action.timer <= 0 ||
      action.toggleTimer <= 0 ||
      action.messageId === "" ||
      action.toggleColor === "" ||
      action.color === "" ||
      action.toggleMessageId === ""
    ) {
      return false;
    }
    return true;
  };

  const onClickInfoButton = () => {
    dispatch(updateOpenModalBankActionsHelp(true));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  useEffect(() => {
    resetForm();
  }, [isSuccessPutAction, isErrorPutAction]);

  useEffect(() => {
    if (formAction === "create") {
      setAction({ ...BLANK_BANK_ACTION });
    }
  }, [formAction]);

  useEffect(() => {
    if (bank && bank.actions) {
      setBankActions(bank.actions);
      setTotalRows(bank.actions.length);
    } else {
      setBankActions([]);
      setTotalRows(0);
    }
  }, [bank]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <TableColumnLiftType liftType={bank.type} />
            <Tag color="orange">{bank.name}</Tag>
            <FontAwesomeIcon
              className="generalStyles__info"
              style={{ fontSize: "18px", marginTop: "2px", cursor: "pointer" }}
              icon={faCircleInfo}
              onClick={onClickInfoButton}
            />
          </div>
        </div>
      </div>
      <Table
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={bankActions}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        scroll={{ x: 900 }}
        size="small"
        className="mt-3"
        loading={isLoadingPutAction || isLoadingGetOneBank}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{totalRows} Rows</Tag>
          </div>
        )}
        rowKey={(record: BankActionInterface) => formatUuidT(record.id)}
      >
        {/** BUTTON TYPE */}
        <ColumnGroup title={t("general.buttonType")}>
          <Column
            title={
              <Select
                showSearch
                optionFilterProp="children"
                style={{ minWidth: "200px", width: "100%" }}
                allowClear={false}
                size="small"
                value={formAction === "create" ? action.buttonType : "TOGGLE"}
                onChange={(e: ActionButtonTypeName) => {
                  onChangeButtonType(e);
                }}
              >
                {/*
                  Derived from ACTION_TYPE_CONFIG rather than listed here. A
                  hardcoded list silently stops offering the next button type
                  added — this select had already fallen behind by two.
                */}
                {(Object.keys(ACTION_TYPE_CONFIG) as ActionButtonTypeName[]).map((item) => (
                  <Option key={item} value={item}>
                    {ACTION_TYPE_CONFIG[item].label}
                  </Option>
                ))}
              </Select>
            }
            dataIndex="buttonType"
            key="buttonType"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <Select
                    showSearch
                    optionFilterProp="children"
                    style={{ minWidth: "200px", width: "100%" }}
                    allowClear={false}
                    size="small"
                    value={action.buttonType}
                    onChange={(e) => {
                      onChangeFormValue(e, "buttonType");
                    }}
                  >
                    {/*
                      Derived from ACTION_TYPE_CONFIG, like the create picker
                      above. This one kept its own hardcoded list of six and so
                      fell behind by three -- ON_OFF, SINGLE and ONE_SHOT were
                      offered when creating a row and unreachable when editing
                      one, on the screen the book recommends for exactly the
                      types the wizard could not set. A row already stored as
                      ON_OFF also rendered a Select whose value was not among
                      its own options.
                    */}
                    {(Object.keys(ACTION_TYPE_CONFIG) as ActionButtonTypeName[]).map((item) => (
                      <Option key={item} value={item}>
                        {ACTION_TYPE_CONFIG[item].label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <TableColumnBankActionType actionType={text || ""} />
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION ON */}
        <ColumnGroup title={t("general.action")}>
          <Column
            title={
              <SelectMessages
                inputValue={formAction === "create" ? action.messageId : ""}
                inputOnChange={onChangeMessage}
                disabledInput={formAction === "edit"}
                displaySelect
                messageType="CONTROL"
              />
            }
            dataIndex="messageId"
            key="messageId"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <SelectMessages
                    inputValue={action.messageId}
                    inputOnChange={onChangeMessage}
                    disabledInput={false}
                    displaySelect
                    messageType="CONTROL"
                  />
                ) : (
                  <SelectMessages
                    inputValue={record.messageId}
                    inputOnChange={() => {}}
                    disabledInput
                    displaySelect={false}
                    messageType="CONTROL"
                  />
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION ON TIMER */}
        <ColumnGroup title={t("general.timer")}>
          <Column
            title={
              <InputNumber
                min={1}
                value={formAction === "create" ? action.timer : 1}
                onChange={(e: any) => {
                  onChangeFormValue(e, "timer");
                }}
                disabled={formAction === "edit" || action.buttonType === "TOGGLE"}
                size="small"
                style={{ minWidth: "100px", width: "100%" }}
              />
            }
            dataIndex="timer"
            key="timer"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <InputNumber
                    min={0}
                    value={action.timer}
                    onChange={(e: any) => {
                      onChangeFormValue(e, "timer");
                    }}
                    size="small"
                    disabled={action.buttonType === "TOGGLE"}
                    style={{ minWidth: "100px", width: "100%" }}
                  />
                ) : (
                  <div> {text} (seconds)</div>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION ON COLOR */}
        <ColumnGroup title={t("general.buttonColor")}>
          <Column
            title={
              <FormInputColorPicker
                setColor={(newValue: string) => {
                  onChangeFormValue(newValue, "color");
                }}
                color={action.color}
              />
            }
            dataIndex="color"
            key="color"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <FormInputColorPicker
                    setColor={(newValue: string) => {
                      onChangeFormValue(newValue, "color");
                    }}
                    color={action.color}
                  />
                ) : (
                  <div className="generalStyles__flex">
                    <div
                      style={{
                        backgroundColor: `${text || "#fffff"}`,
                        height: "20px",
                        width: "20px",
                        marginLeft: "25px",
                        borderRadius: "100px",
                      }}
                    />
                    <div className="generalStyles__mlFix">{text}</div>
                  </div>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION LABEL */}
        <ColumnGroup title={t("general.label")}>
          <Column
            title={
              <Input
                size="small"
                style={{ minWidth: "150px", width: "100%" }}
                value={formAction === "create" ? action.label : ""}
                onChange={(e) => {
                  onChangeFormValue(e.target.value, "label");
                }}
                disabled={formAction === "edit"}
                placeholder="Enter label"
              />
            }
            dataIndex="label"
            key="label"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <Input
                    size="small"
                    style={{ minWidth: "150px", width: "100%" }}
                    value={action.label}
                    onChange={(e) => {
                      onChangeFormValue(e.target.value, "label");
                    }}
                    placeholder="Enter label"
                  />
                ) : (
                  <p>{text || "-"}</p>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** VALID STATES */}
        <ColumnGroup title="Valid States">
          <Column
            title={
              <SelectMessagesMultiple
                inputValue={formAction === "create" ? action.validStates : []}
                inputOnChange={onChangeValidStates}
                disabledInput={formAction === "edit"}
                displaySelect
              />
            }
            dataIndex="validStates"
            key="validStates"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <SelectMessagesMultiple
                    inputValue={action.validStates}
                    inputOnChange={onChangeValidStates}
                    disabledInput={false}
                    displaySelect
                  />
                ) : (
                  <SelectMessagesMultiple
                    inputValue={record.validStates}
                    inputOnChange={() => {}}
                    disabledInput
                    displaySelect={false}
                  />
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION OFF */}
        <ColumnGroup title="Toggle Action">
          <Column
            title={
              <SelectMessages
                inputValue={formAction === "create" ? action.toggleMessageId : ""}
                inputOnChange={onChangeToggleMessage}
                disabledInput={formAction === "edit"}
                displaySelect
                messageType="CONTROL"
              />
            }
            dataIndex="toggleMessageId"
            key="toggleMessageId"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <SelectMessages
                    inputValue={action.toggleMessageId}
                    inputOnChange={onChangeToggleMessage}
                    disabledInput={false}
                    displaySelect
                    messageType="CONTROL"
                  />
                ) : (
                  <SelectMessages
                    inputValue={record.toggleMessageId}
                    inputOnChange={() => {}}
                    disabledInput
                    displaySelect={false}
                    messageType="CONTROL"
                  />
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION OFF TIMER */}
        <ColumnGroup title="Toggle Timer">
          <Column
            title={
              <InputNumber
                min={1}
                value={formAction === "create" ? action.toggleTimer : 1}
                onChange={(e: any) => {
                  onChangeFormValue(e, "toggleTimer");
                }}
                disabled={
                  formAction === "edit" ||
                  action.buttonType === "TOGGLE" ||
                  action.buttonType === "HOLDBACK" ||
                  action.buttonType === "TURNBACK" ||
                  action.buttonType === "ENERGIZER_TURN_BACK"
                }
                size="small"
                style={{ minWidth: "100px", width: "100%" }}
              />
            }
            dataIndex="toggleTimer"
            key="toggleTimer"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <InputNumber
                    min={0}
                    value={action.toggleTimer}
                    onChange={(e: any) => {
                      onChangeFormValue(e, "toggleTimer");
                    }}
                    size="small"
                    disabled={
                      action.buttonType === "TOGGLE" ||
                      action.buttonType === "HOLDBACK" ||
                      action.buttonType === "TURNBACK" ||
                      action.buttonType === "ENERGIZER_TURN_BACK"
                    }
                    style={{ minWidth: "100px", width: "100%" }}
                  />
                ) : (
                  <div> {text} (seconds)</div>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** ACTION OFF COLOR */}
        <ColumnGroup title="Toggle Button Color">
          <Column
            title={
              <FormInputColorPicker
                setColor={(newValue: string) => {
                  onChangeFormValue(newValue, "toggleColor");
                }}
                color={action.toggleColor}
              />
            }
            dataIndex="toggleColor"
            key="toggleColor"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <FormInputColorPicker
                    setColor={(newValue: string) => {
                      onChangeFormValue(newValue, "toggleColor");
                    }}
                    color={action.toggleColor}
                  />
                ) : (
                  <div className="generalStyles__flex">
                    <div
                      style={{
                        backgroundColor: `${text || "#fffff"}`,
                        height: "20px",
                        width: "20px",
                        marginLeft: "25px",
                        borderRadius: "100px",
                      }}
                    />
                    <div className="generalStyles__mlFix">{text}</div>
                  </div>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** TOGGLE LABEL */}
        <ColumnGroup title="Toggle Label">
          <Column
            title={
              <Input
                size="small"
                style={{ minWidth: "150px", width: "100%" }}
                value={formAction === "create" ? action.toggleLabel : ""}
                onChange={(e) => {
                  onChangeFormValue(e.target.value, "toggleLabel");
                }}
                disabled={formAction === "edit"}
                placeholder="Enter toggle label"
              />
            }
            dataIndex="toggleLabel"
            key="toggleLabel"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <Input
                    size="small"
                    style={{ minWidth: "150px", width: "100%" }}
                    value={action.toggleLabel}
                    onChange={(e) => {
                      onChangeFormValue(e.target.value, "toggleLabel");
                    }}
                    placeholder="Enter toggle label"
                  />
                ) : (
                  <p>{text || "-"}</p>
                )}
              </div>
            )}
          />
        </ColumnGroup>
        {/** TOGGLE VALID STATES */}
        <ColumnGroup title="Toggle Valid States">
          <Column
            title={
              <SelectMessagesMultiple
                inputValue={formAction === "create" ? action.toggleValidStates : []}
                inputOnChange={onChangeToggleValidStates}
                disabledInput={formAction === "edit"}
                displaySelect
              />
            }
            dataIndex="toggleValidStates"
            key="toggleValidStates"
            render={(text, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <SelectMessagesMultiple
                    inputValue={action.toggleValidStates}
                    inputOnChange={onChangeToggleValidStates}
                    disabledInput={false}
                    displaySelect
                  />
                ) : (
                  <SelectMessagesMultiple
                    inputValue={record.toggleValidStates}
                    inputOnChange={() => {}}
                    disabledInput
                    displaySelect={false}
                  />
                )}
              </div>
            )}
          />
        </ColumnGroup>

        {/** ACTIONS */}
        <ColumnGroup align="center" title={t("edgeManagement.banks.actions")}>
          <Column
            align="center"
            title={
              <div className="generalStyles__flexCenter">
                <div>
                  <Tooltip title={t("general.save")}>
                    <Button
                      className={formAction === "create" && formIsValid() ? "buttonStyle__10" : ""}
                      disabled={!formIsValid() || formAction === "edit"}
                      onClick={onClickSave}
                      icon={<FontAwesomeIcon icon={faSave} />}
                    />
                  </Tooltip>
                </div>
                <div className="generalStyles__mlFix">
                  <Tooltip title={t("general.clear")}>
                    <Button
                      className={formAction === "create" ? "buttonStyle__14" : ""}
                      onClick={resetForm}
                      disabled={formAction === "edit"}
                      icon={<FontAwesomeIcon icon={faX} />}
                    />
                  </Tooltip>
                </div>
              </div>
            }
            key="action"
            render={(_: any, record: BankActionInterface) => (
              <div>
                {formAction === "edit" && record.id === action.id ? (
                  <div className="generalStyles__flexCenter">
                    <div>
                      <Tooltip title={t("general.save")}>
                        <Button
                          className="buttonStyle__10"
                          disabled={!formIsValid()}
                          onClick={onClickSave}
                          icon={<FontAwesomeIcon icon={faSave} />}
                        />
                      </Tooltip>
                    </div>
                    <div className="generalStyles__mlFix">
                      <Tooltip title={t("general.cancel")}>
                        <Button
                          className="buttonStyle__14"
                          onClick={resetForm}
                          icon={<FontAwesomeIcon icon={faX} />}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <BankActionsTableActions selectedItem={record} />
                )}
              </div>
            )}
          />
        </ColumnGroup>
      </Table>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPutAction}
        isSuccess={isSuccessPutAction}
        requestType="PUT"
        error={errorPutAction}
        name="Action"
      />
    </div>
  );
}
